import { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Loader2, GraduationCap, CalendarCheck, Gift, MessageSquare, Copy, Wallet, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

const ENR_STATUS = {
  enrolled: { label: 'On course', cls: 'bg-pink-100 text-pink-700' },
  completed: { label: 'Completed', cls: 'bg-green-100 text-green-700' },
  withdrawn: { label: 'Withdrew', cls: 'bg-slate-200 text-slate-600' },
};
const REG_STATUS = {
  confirmed: 'Booked', attended: 'Attended', no_show: 'No-show', cancelled: 'Cancelled', rescheduled: 'Rescheduled', enrolled: 'Enrolled',
};
const MARK = {
  present: 'bg-green-100 text-green-700', late: 'bg-amber-100 text-amber-700',
  absent: 'bg-red-100 text-red-700', excused: 'bg-slate-200 text-slate-600',
};
const REASON = { sick: 'Sick', family: 'Family', transport: 'Transport', work: 'Work', childcare: 'Childcare', no_reason: 'No reason', other: 'Other' };
const SOURCE = { own: 'Walk-in / own', manual: 'Walk-in / own', platform: 'Ads', referral: 'Referral', ghl_webhook: 'GHL / website' };

const d = (iso, opts = { day: 'numeric', month: 'short', year: 'numeric' }) =>
  iso ? new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString('en-GB', opts) : '';
const gbp = (v) => `£${Number(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 2 })}`;

const Section = ({ icon: Icon, title, children }) => (
  <div className="space-y-2">
    <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
      <Icon className="w-3.5 h-3.5" /> {title}
    </p>
    {children}
  </div>
);

const CourseCard = ({ c }) => {
  const [open, setOpen] = useState(c.status === 'enrolled');
  const st = ENR_STATUS[c.status] || ENR_STATUS.enrolled;
  const paid = c.price != null ? Math.max(Number(c.price) - Number(c.referral_discount || 0) - Number(c.credit_applied || 0), 0) : null;
  const absences = c.marks.filter(m => m.status === 'absent' || m.status === 'excused');
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{c.course}</p>
          <p className="text-xs text-slate-500">
            {d(c.start_date, { day: 'numeric', month: 'short' })} – {d(c.end_date)}{c.location ? ` · ${c.location}` : ''}
          </p>
        </div>
        <Badge className={`text-[11px] ${st.cls}`}>{st.label}</Badge>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500 mt-2">
        {c.enrollment_date && <span>Enrolled {d(c.enrollment_date)}</span>}
        {c.completion_date && <span>Completed {d(c.completion_date)}</span>}
        {c.withdrawn_at && <span>Withdrew {d(c.withdrawn_at)}{c.withdrawn_reason ? ` (${c.withdrawn_reason})` : ''}</span>}
        {paid != null && <span>Price {gbp(paid)}{(c.referral_discount || c.credit_applied) ? ` (after ${gbp(Number(c.referral_discount || 0) + Number(c.credit_applied || 0))} off)` : ''}</span>}
      </div>
      <div className="mt-2">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className={c.stats.percentage != null && c.stats.percentage < 80 ? 'text-red-600 font-semibold' : 'text-slate-600'}>
            Attendance {c.stats.percentage != null ? `${c.stats.percentage}%` : '—'}
          </span>
          <span className="text-slate-400">
            {c.stats.present} present · {c.stats.late} late · {c.stats.absent} absent · {c.stats.excused} excused
          </span>
        </div>
        <Progress value={c.stats.percentage || 0} className={`h-1.5 ${c.stats.percentage != null && c.stats.percentage < 80 ? '[&>div]:bg-red-500' : ''}`} />
      </div>
      {c.marks.length > 0 && (
        <button type="button" onClick={() => setOpen(o => !o)} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-pink-600">
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {open ? 'Hide' : 'Show'} every class ({c.marks.length}){absences.length ? ` · ${absences.length} missed` : ''}
        </button>
      )}
      {open && c.marks.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {c.marks.map(m => (
            <span key={m.date} title={[m.status, m.reason ? REASON[m.reason] : null, m.note].filter(Boolean).join(' · ')}
              className={`text-[11px] rounded-md px-1.5 py-0.5 ${MARK[m.status] || 'bg-slate-100 text-slate-500'}`}>
              {d(m.date, { day: 'numeric', month: 'short' })}{m.reason ? ` · ${REASON[m.reason] || m.reason}` : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const StudentProfilePanel = ({ leadId, refreshKey }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      const res = await fetch(`${API_URL}/api/training/students/${leadId}/profile`, { credentials: 'include' });
      if (!res.ok) throw new Error();
      setData(await res.json());
    } catch {
      setError(true);
    }
  }, [leadId]);

  useEffect(() => { if (leadId) load(); }, [leadId, load, refreshKey]);

  if (error) return <p className="text-sm text-red-600 py-6 text-center">Couldn't load this student.</p>;
  if (!data) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-pink-400 animate-spin" /></div>;

  const { lead, courses, registrations, referral, messages } = data;
  const current = courses.filter(c => c.status === 'enrolled');
  const past = courses.filter(c => c.status !== 'enrolled');

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-xl bg-slate-50 p-2.5">
          <p className="text-slate-400">Came from</p>
          <p className="font-medium text-slate-800">{SOURCE[lead.lead_source_type] || SOURCE[lead.source] || lead.source || '—'}</p>
        </div>
        <div className="rounded-xl bg-slate-50 p-2.5">
          <p className="text-slate-400">Added</p>
          <p className="font-medium text-slate-800">{d(lead.created_at) || '—'}</p>
        </div>
        <div className="rounded-xl bg-slate-50 p-2.5">
          <p className="text-slate-400">Interested in</p>
          <p className="font-medium text-slate-800 truncate">{lead.course_interest || lead.course_name || '—'}{lead.course_price ? ` · ${gbp(lead.course_price)}` : ''}</p>
        </div>
        <div className={`rounded-xl p-2.5 ${lead.deposit_paid ? 'bg-green-50' : 'bg-slate-50'}`}>
          <p className="text-slate-400 flex items-center gap-1"><Wallet className="w-3 h-3" /> Deposit</p>
          <p className={`font-medium ${lead.deposit_paid ? 'text-green-700' : 'text-slate-800'}`}>
            {lead.deposit_paid ? `Paid${lead.deposit_amount ? ` ${gbp(lead.deposit_amount)}` : ''}${lead.deposit_paid_at ? ` · ${d(lead.deposit_paid_at, { day: 'numeric', month: 'short' })}` : ''}` : 'Not paid'}
          </p>
        </div>
      </div>

      <Section icon={GraduationCap} title={`Courses (${courses.length})`}>
        {courses.length === 0 ? (
          <p className="text-sm text-slate-400">Not enrolled on any course yet.</p>
        ) : (
          <div className="space-y-2">
            {current.map(c => <CourseCard key={c.enrollment_id} c={c} />)}
            {past.length > 0 && current.length > 0 && <p className="text-[11px] text-slate-400 pt-1">Previous courses</p>}
            {past.map(c => <CourseCard key={c.enrollment_id} c={c} />)}
          </div>
        )}
      </Section>

      <Section icon={CalendarCheck} title={`Registrations (${registrations.length})`}>
        {registrations.length === 0 ? (
          <p className="text-sm text-slate-400">No registration appointments.</p>
        ) : (
          <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
            {registrations.map(r => (
              <div key={r.registration_id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                <span className="text-slate-800">
                  {d(r.registration_date, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}{r.registration_time ? ` · ${r.registration_time}` : ''}
                  {r.course_interest ? <span className="text-slate-400"> · {r.course_interest}</span> : null}
                </span>
                <Badge className="text-[11px] bg-slate-100 text-slate-700">{REG_STATUS[r.status] || r.status}</Badge>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section icon={Gift} title="Referrals">
        <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-1.5 text-sm">
          <p className="text-slate-700">{referral.referred_by ? <>Referred by <span className="font-medium">{referral.referred_by.name}</span></> : 'Not referred by anyone'}</p>
          <p className="text-slate-700">
            Referred {referral.friends.length} friend{referral.friends.length === 1 ? '' : 's'}
            {referral.friends.length ? `: ${referral.friends.map(f => f.name).join(', ')}` : ''}
          </p>
          {(referral.credit_available > 0 || referral.credit_used > 0) && (
            <p className="text-slate-700">Credit: <span className="font-medium text-green-700">{gbp(referral.credit_available)} available</span>{referral.credit_used ? ` · ${gbp(referral.credit_used)} used` : ''}</p>
          )}
          {referral.link ? (
            <button type="button" className="inline-flex items-center gap-1 text-xs font-medium text-pink-600"
              onClick={() => navigator.clipboard?.writeText(referral.link).then(() => toast.success('Referral link copied'))}>
              <Copy className="w-3.5 h-3.5" /> Copy their referral link ({referral.code}){referral.invite_sent ? ' · invite sent' : ''}
            </button>
          ) : (
            <p className="text-xs text-slate-400">No referral link yet (created when they enrol or pay a deposit).</p>
          )}
        </div>
      </Section>

      {messages.length > 0 && (
        <Section icon={MessageSquare} title="Messages sent">
          <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
            {messages.slice(0, 10).map(m => (
              <div key={m.message_id} className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
                <span className="text-slate-700">
                  {m.kind === 'class_reminder' ? 'Class reminder' : m.kind === 'absence' ? '"We missed you"' : 'Message'} · {m.channel === 'sms' ? 'Text' : 'Email'}
                </span>
                <span className={m.status === 'sent' ? 'text-green-700' : 'text-slate-400'}>{m.status} · {d(m.created_at, { day: 'numeric', month: 'short' })}</span>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
};

export default StudentProfilePanel;
